<template>
  <div class="vectorization-sync-page">
    <b-overlay :show="displayOverlay" rounded="sm" spinner-variant="primary" opacity="0.75">
      <b-card no-body class="sync-card">
        <b-card-header class="sync-card-header d-flex justify-content-between align-items-center">
          <div>
            <h4 class="mb-0">Sincronizacao para Vetorizacao</h4>
            <small class="text-muted">Selecione o periodo e os filtros para enviar noticias ao processamento.</small>
          </div>
          <b-badge variant="light-primary" pill>
            {{ selectedOriginsCount }} origem(ns) / {{ selectedChannelsCount }} canal(is) / {{ selectedSubjectsCount }} assunto(s)
          </b-badge>
        </b-card-header>

        <b-card-body>
          <b-row class="result-row mb-1 mt-1" v-if="syncResult.hasRun">
            <b-col md="4" class="mb-1 mb-md-0">
              <div class="result-box result-success">
                <div class="result-label">Vetorizadas</div>
                <div class="result-value">{{ formatResultValue(syncResult.vectorized) }}</div>
              </div>
            </b-col>
            <b-col md="4" class="mb-1 mb-md-0">
              <div class="result-box result-warning">
                <div class="result-label">Nao vetorizadas</div>
                <div class="result-value">{{ formatResultValue(syncResult.notVectorized) }}</div>
              </div>
            </b-col>
            <b-col md="4">
              <div class="result-box result-danger">
                <div class="result-label">Erro na vetorizacao</div>
                <div class="result-value">{{ formatResultValue(syncResult.error) }}</div>
              </div>
            </b-col>
            <b-col md="12" class="mt-1">
              <small class="text-muted">
                Ultima execucao: {{ syncResult.lastRunAt }}
                <span v-if="syncResult.requested !== null"> | Solicitadas: {{ syncResult.requested }}</span>
                <span v-if="syncResult.message"> | Status: {{ syncResult.message }}</span>
              </small>
            </b-col>

            <b-col md="12" class="mt-1" v-if="syncResult.errorNews && syncResult.errorNews.length">
              <div class="error-news-box">
                <div class="error-news-title">Noticias com erro na vetorizacao</div>
                <b-table
                  :items="syncResult.errorNews"
                  :fields="errorNewsFields"
                  small
                  bordered
                  hover
                  empty-text="Nenhum erro retornado"
                />
              </div>
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col md="6">
              <b-form-group label="De" label-for="dt-from">
                <div class="d-flex align-items-center date-input-wrapper">
                  <feather-icon class="mr-1 text-primary" icon="CalendarIcon" size="16" />
                  <flat-pickr
                    id="dt-from"
                    v-model="form.dtFrom"
                    class="form-control"
                    :config="datePickerConfig"
                    placeholder="Data inicial"
                  />
                </div>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Ate" label-for="dt-to">
                <div class="d-flex align-items-center date-input-wrapper">
                  <feather-icon class="mr-1 text-primary" icon="CalendarIcon" size="16" />
                  <flat-pickr
                    id="dt-to"
                    v-model="form.dtTo"
                    class="form-control"
                    :config="datePickerConfig"
                    placeholder="Data final"
                  />
                </div>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group label="Origens" label-for="origin-list">
                <v-select
                  id="origin-list"
                  v-model="form.origins"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="origins"
                  label="name"
                  multiple
                  :close-on-select="false"
                  @input="onOriginChanged"
                >
                  <span slot="no-options">{{ $t('GENERAL.NoOptions') }}</span>
                </v-select>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group label="Canais" label-for="channel-list">
                <v-select
                  id="channel-list"
                  v-model="form.channels"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="channels"
                  label="name"
                  multiple
                  :close-on-select="false"
                >
                  <span slot="no-options">{{ $t('GENERAL.NoOptions') }}</span>
                </v-select>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group label="Conteudo" label-for="content-list">
                <v-select
                  id="content-list"
                  v-model="form.contents"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="contents"
                  label="name"
                  multiple
                  :close-on-select="false"
                >
                  <span slot="no-options">{{ $t('GENERAL.NoOptions') }}</span>
                </v-select>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group label="Assuntos" label-for="subject-list">
                <v-select
                  id="subject-list"
                  v-model="form.subjects"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="subjects"
                  label="name"
                  multiple
                  :close-on-select="false"
                >
                  <span slot="no-options">{{ $t('GENERAL.NoOptions') }}</span>
                </v-select>
              </b-form-group>
            </b-col>

            <b-col md="12" class="mt-1">
              <b-form-checkbox v-model="useNewsIdsMode" switch>
                Vetorizar por IDs especificos
              </b-form-checkbox>
            </b-col>
          </b-row>

          <div v-if="useNewsIdsMode" class="news-id-mode mt-2">
            <b-row>
              <b-col md="8">
                <b-form-group label="Buscar noticias para selecionar IDs" label-for="news-search-query">
                  <b-input-group>
                    <b-form-input
                      id="news-search-query"
                      v-model="newsSearchQuery"
                      placeholder="Buscar por titulo ou conteudo"
                    />
                    <b-input-group-append>
                      <b-button variant="outline-primary" @click="fetchCandidateNews" :disabled="candidateNewsLoading">
                        Buscar noticias
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
                <b-form-checkbox v-model="onlyNotVectorized" switch>
                  Buscar apenas noticias nao vetorizadas
                </b-form-checkbox>
              </b-col>
              <b-col md="4" class="d-flex align-items-end justify-content-md-end mb-1">
                <small class="text-muted">Selecionadas na lista: {{ selectedNewsIds.length }}</small>
              </b-col>
            </b-row>

            <b-overlay :show="candidateNewsLoading" rounded="sm" spinner-variant="primary" opacity="0.6">
              <div class="table-responsive mb-1">
                <b-table
                  class="candidate-news-table"
                  :items="candidateNews"
                  :fields="newsFields"
                  small
                  bordered
                  hover
                  empty-text="Nenhuma noticia encontrada"
                  >
                  <!-- responsive
                  show-empty -->
                  <template #cell(select)="row">
                    <b-form-checkbox
                      :checked="selectedNewsIds.includes(row.item._id)"
                      @change="toggleNewsSelection(row.item._id, $event)"
                    />
                  </template>

                  <template #cell(contentPreview)="row">
                    <div class="">{{ row.item.contentPreview }}</div>
                  </template>
                </b-table>
              </div>
            </b-overlay>

            <b-form-group label="Inserir IDs manualmente" label-for="manual-news-ids">
              <b-form-textarea
                id="manual-news-ids"
                v-model="manualNewsIds"
                rows="4"
                placeholder="Cole os IDs separados por virgula, espaco ou quebra de linha"
              />
            </b-form-group>
          </div>

          <div class="d-flex align-items-center justify-content-end mt-2">
            <b-button variant="outline-secondary" class="mr-1" @click="clearFilters">
              Limpar
            </b-button>
            <b-button variant="primary" @click="synchronizeNewsData">
              <feather-icon icon="RefreshCcwIcon" size="14" class="mr-50" />
              Sincronizar Noticias
            </b-button>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BInputGroup,
  BInputGroupAppend,
  BOverlay,
  BRow,
  BTable,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import dropdownService from '@/providers/dropdownService'
import newsService from '@/providers/newsService'
import messagesNotifyHelper from '@/helpers/messages-notify-helper'
import convertDatetimeHelper from '@/helpers/convert-datetime-helper'

export default {
  name: 'VectorizationManagementSincronizeData',
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BOverlay,
    BRow,
    BTable,
    flatPickr,
    vSelect,
  },
  data() {
    return {
      displayOverlay: false,
      origins: [],
      channels: [],
      contents: [],
      subjects: [],
      form: {
        dtFrom: convertDatetimeHelper.convertDateParams(convertDatetimeHelper.getFirstDayOfCurrentYear()),
        dtTo: convertDatetimeHelper.convertDateParams(convertDatetimeHelper.getLastDayOfCurrentYear()),
        origins: [],
        channels: [],
        contents: [],
        subjects: [],
      },
      useNewsIdsMode: false,
      onlyNotVectorized: false,
      syncPollingTimer: null,
      candidateNewsLoading: false,
      candidateNews: [],
      selectedNewsIds: [],
      manualNewsIds: '',
      newsSearchQuery: '',
      newsFields: [
        { key: 'select', label: '', thStyle: { width: '60px' } },
        { key: '_id', label: 'ID' },
        { key: 'title', label: 'Titulo' },
        { key: 'dt_publish', label: 'Publicacao' },
        { key: 'layout', label: 'Layout' },
        { key: 'contentPreview', label: 'Conteudo' },
      ],
      errorNewsFields: [
        { key: 'newsId', label: 'ID da Noticia' },
        { key: 'reason', label: 'Motivo' },
      ],
      syncResult: {
        hasRun: false,
        vectorized: null,
        notVectorized: null,
        error: null,
        requested: null,
        message: '',
        lastRunAt: '',
        errorNews: [],
      },
      datePickerConfig: {
        enableTime: false,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y',
        allowInput: true,
      },
    }
  },
  computed: {
    selectedOriginsCount() {
      return this.form.origins.length
    },
    selectedChannelsCount() {
      return this.form.channels.length
    },
    selectedSubjectsCount() {
      return this.form.subjects.length
    },
  },
  mounted() {
    this.loadDropdowns()
  },
  methods: {
    loadDropdowns() {
      Promise.all([
        dropdownService.getAllOrigin(new URLSearchParams()),
        dropdownService.getChannel(new URLSearchParams()),
        dropdownService.getAllSubjects(new URLSearchParams()),
      ])
        .then(([origins, channels, subjects]) => {
          this.origins = origins
          this.channels = channels
          this.subjects = subjects
          return this.loadContentsByOrigins()
        })
        .catch((err) => {
          console.log(err)
          messagesNotifyHelper.errorNotify('Sincronizacao', 'Erro ao carregar origens, canais e conteudos.')
        })
    },
    getEntityId(item) {
      return item?._id || item?.id || null
    },
    loadContentsByOrigins() {
      const params = new URLSearchParams()
      const originIds = this.form.origins.map((item) => this.getEntityId(item)).filter((id) => !!id)
      if (originIds.length) {
        params.append('origins', originIds.join(','))
      }

      return dropdownService
        .getContent(params)
        .then((res) => {
          this.contents = Array.isArray(res) ? res : []
          const contentIds = this.contents.map((item) => this.getEntityId(item))
          this.form.contents = this.form.contents.filter((item) => contentIds.includes(this.getEntityId(item)))
        })
        .catch((err) => {
          console.log(err)
          this.contents = []
          this.form.contents = []
        })
    },
    fetchCandidateNews() {
      const params = new URLSearchParams()
      params.append('page', 1)
      params.append('limit', 30)
      params.append('dt_range', `${this.form.dtFrom} até ${this.form.dtTo}`)
      params.append('dt_end', this.form.dtTo)
      if (this.form.origins.length) {
        params.append('source', this.form.origins.map((item) => this.getEntityId(item)).join(','))
      }
      if (this.form.contents.length) {
        params.append('content', this.form.contents.map((item) => this.getEntityId(item)).join(','))
      }
      if (this.form.subjects.length) {
        params.append('subject', this.form.subjects.map((item) => this.getEntityId(item)).join(','))
      }
      if (this.newsSearchQuery) {
        params.append('searchQuery', this.newsSearchQuery)
      }
      if (this.onlyNotVectorized) {
        params.append('notVectorized', true)
      }
      params.append('event_status', 1)

      this.candidateNewsLoading = true
      newsService
        .manageNewsReduced(params)
        .then((res) => {
          const items = Array.isArray(res?.data) ? res.data : []
          this.candidateNews = items.map((item) => ({
            _id: item._id,
            title: this.getNewsTitle(item),
            dt_publish: this.getPublishDate(item?.dt_publish),
            layout: this.getLayoutLabel(item?.layout),
            contentPreview: this.getNewsContentPreview(item),
          }))
          this.selectedNewsIds = this.selectedNewsIds.filter((id) => this.candidateNews.some((news) => news._id === id))
        })
        .catch((err) => {
          console.log(err)
          messagesNotifyHelper.errorNotify('Noticias', 'Erro ao buscar lista de noticias.')
        })
        .finally(() => {
          this.candidateNewsLoading = false
        })
    },
    getNewsTitle(item) {
      return item?.title || item?.content?.title?.data || '-'
    },
    getPublishDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('pt-BR')
    },
    getLayoutLabel(layout) {
      const code = typeof layout === 'object' ? layout?.id || layout?._id || layout?.code || layout?.value : layout
      const numericCode = Number(code)

      if (numericCode === 1) return 'Notícia'
      if (numericCode === 2) return 'Pisca'
      if (numericCode === 3) return 'Podcast'
      if (numericCode === 4) return 'TVCast'
      if (numericCode === 5) return 'Relatório'
      if (numericCode === 6) return 'Evento'

      if (typeof layout === 'string') return layout
      return layout?.name || layout?.description || '-'
    },
    getNewsContentPreview(item) {
      const rawContent = item?.content || item?.content?.body?.data || item?.content?.text?.data || ''
      const content = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent)
      return content.replace(/<[^>]*>?/gm, '').slice(0, 120) || '-'
    },
    toggleNewsSelection(newsId, checked) {
      if (checked) {
        if (!this.selectedNewsIds.includes(newsId)) {
          this.selectedNewsIds.push(newsId)
        }
        return
      }

      this.selectedNewsIds = this.selectedNewsIds.filter((item) => item !== newsId)
    },
    parseManualNewsIds() {
      return this.manualNewsIds
        .split(/[\s,;]+/)
        .map((item) => item.trim())
        .filter((item) => !!item)
    },
    getAllSelectedNewsIds() {
      return Array.from(new Set([...this.selectedNewsIds, ...this.parseManualNewsIds()]))
    },
    extractCount(data, keys) {
      const buckets = [data, data?.data, data?.result, data?.summary].filter((item) => !!item)
      for (let i = 0; i < buckets.length; i++) {
        const bucket = buckets[i]
        for (let j = 0; j < keys.length; j++) {
          const value = bucket[keys[j]]
          if (typeof value === 'number') return value
          if (Array.isArray(value)) return value.length
        }
      }
      return null
    },
    extractItems(data, keys) {
      const buckets = [data, data?.data, data?.result, data?.summary].filter((item) => !!item)
      for (let i = 0; i < buckets.length; i++) {
        const bucket = buckets[i]
        for (let j = 0; j < keys.length; j++) {
          const value = bucket[keys[j]]
          if (Array.isArray(value)) return value
        }
      }
      return []
    },
    updateSyncResult(response, requestedCount) {
      const payload = response?.data || response || {}
      const vectorized = this.extractCount(payload, ['vectorized', 'vectorizedCount', 'totalVectorized', 'successCount', 'vectorizedNews'])
      const notVectorized = this.extractCount(payload, ['notVectorized', 'notVectorizedCount', 'totalNotVectorized', 'ignoredCount', 'notVectorizedNews'])
      const error = this.extractCount(payload, ['error', 'errorCount', 'totalError', 'failedCount', 'errorNews'])
      const errorNews = this.extractItems(payload, ['errorNews', 'errors', 'failedNews']).map((item) => ({
        newsId: item?.newsId || item?._id || '-',
        reason: item?.reason || item?.message || 'erro_nao_detalhado',
      }))

      this.syncResult = {
        hasRun: true,
        vectorized,
        notVectorized,
        error,
        requested: typeof payload?.requested === 'number' ? payload.requested : requestedCount,
        message: typeof payload === 'string' ? payload : payload?.message || 'Processamento finalizado',
        lastRunAt: new Date().toLocaleString('pt-BR'),
        errorNews,
      }
    },
    formatResultValue(value) {
      return value === null || value === undefined ? '-' : value
    },
    onOriginChanged() {
      this.form.channels = []
      this.form.contents = []

      if (!this.form.origins.length) {
        Promise.all([
          dropdownService.getChannel(new URLSearchParams()),
          this.loadContentsByOrigins(),
        ])
          .then(([res]) => {
            this.channels = res
          })
          .catch((err) => console.log(err))
        return
      }

      const params = new URLSearchParams()
      params.append('origins', this.form.origins.map((item) => this.getEntityId(item)).join(','))
      Promise.all([
        dropdownService.getChannelByOrigin(params),
        this.loadContentsByOrigins(),
      ])
        .then(([res]) => {
          this.channels = res
        })
        .catch((err) => console.log(err))
    },
    clearFilters() {
      this.form.origins = []
      this.form.channels = []
      this.form.contents = []
      this.form.subjects = []
      this.form.dtFrom = convertDatetimeHelper.convertDateToStringFormatFrom(convertDatetimeHelper.getFirstDayOfCurrentYear())
      this.form.dtTo = convertDatetimeHelper.convertDateToStringFormatFrom(convertDatetimeHelper.getLastDayOfCurrentYear())
      this.useNewsIdsMode = false
      this.onlyNotVectorized = false
      this.selectedNewsIds = []
      this.manualNewsIds = ''
      this.newsSearchQuery = ''
      this.candidateNews = []
      this.onOriginChanged()
    },
    synchronizeNewsData() {
      if (!this.form.dtFrom || !this.form.dtTo) {
        messagesNotifyHelper.warningNotify('Sincronizacao', 'Informe as datas inicial e final.')
        return
      }

      const newsIds = this.useNewsIdsMode ? this.getAllSelectedNewsIds() : []
      if (this.useNewsIdsMode && !newsIds.length) {
        messagesNotifyHelper.warningNotify('Sincronizacao', 'Selecione noticias na lista ou informe IDs manualmente.')
        return
      }

      const params = new URLSearchParams()
      const newForm = {
        dtFrom: this.form.dtFrom,
        dtTo: this.form.dtTo,
        origins: this.form.origins.map((item) => this.getEntityId(item)).filter((id) => !!id),
        channels: this.form.channels.map((item) => this.getEntityId(item)).filter((id) => !!id),
        content: this.form.contents.map((item) => this.getEntityId(item)).filter((id) => !!id),
        subjects: this.form.subjects.map((item) => this.getEntityId(item)).filter((id) => !!id),
        newsIds,
      }

      this.stopSyncPolling()
      this.displayOverlay = true
      newsService
        .syncNewsWithVectorization(newForm, params)
        .then((response) => {
          const jobId = response?.jobId || response?.data?.jobId
          if (!jobId) {
            // Fallback for a response that already came back with the final summary (older backend contract)
            this.updateSyncResult(response, this.useNewsIdsMode ? newsIds.length : null)
            this.displayOverlay = false
            return
          }

          messagesNotifyHelper.successNotify('Sincronizacao', 'Processo de sincronizacao iniciado com sucesso, aguardando finalizacao...')
          this.pollSyncJob(jobId)
        })
        .catch((err) => {
          console.log(err)
          const errorMessage = err?.response?.data?.message || 'Erro ao iniciar sincronizacao das noticias.'
          this.syncResult = {
            hasRun: true,
            vectorized: null,
            notVectorized: null,
            error: null,
            requested: this.useNewsIdsMode ? newsIds.length : null,
            message: errorMessage,
            lastRunAt: new Date().toLocaleString('pt-BR'),
            errorNews: [],
          }
          messagesNotifyHelper.errorNotify('Sincronizacao', 'Erro ao iniciar sincronizacao das noticias.')
          this.displayOverlay = false
        })
    },
    stopSyncPolling() {
      if (this.syncPollingTimer) {
        clearTimeout(this.syncPollingTimer)
        this.syncPollingTimer = null
      }
    },
    pollSyncJob(jobId) {
      newsService
        .getVectorizationSyncJob(jobId, new URLSearchParams())
        .then((job) => {
          this.updateSyncResult(job, null)

          const status = job?.status || job?.data?.status
          if (status === 'processing') {
            this.syncPollingTimer = setTimeout(() => this.pollSyncJob(jobId), 4000)
            return
          }

          this.displayOverlay = false
          if (status === 'error') {
            messagesNotifyHelper.errorNotify('Sincronizacao', 'Erro ao processar sincronizacao das noticias.')
          } else {
            messagesNotifyHelper.successNotify('Sincronizacao', 'Processo de sincronizacao finalizado.')
          }
        })
        .catch((err) => {
          console.log(err)
          this.displayOverlay = false
          messagesNotifyHelper.errorNotify('Sincronizacao', 'Erro ao consultar status da sincronizacao.')
        })
    },
  },
  beforeDestroy() {
    this.stopSyncPolling()
  },
}
</script>

<style lang="scss" scoped>
.vectorization-sync-page {
  .sync-card {
    border: 0;
    border-radius: 0.75rem;
    box-shadow: 0 12px 24px rgba(34, 41, 47, 0.08);
  }

  .sync-card-header {
    background: linear-gradient(120deg, #f8fafc 0%, #eef2f7 100%);
    border-bottom: 1px solid #dfe6ef;
  }

  .result-box {
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    border: 1px solid transparent;
  }

  .result-label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .result-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .result-success {
    background-color: #e8f7ef;
    border-color: #bde7cf;
    color: #11663a;
  }

  .result-warning {
    background-color: #fff6dd;
    border-color: #ffe2a3;
    color: #7a4c00;
  }

  .result-danger {
    background-color: #fdecef;
    border-color: #f7c6cf;
    color: #8c1d31;
  }

  .error-news-box {
    border: 1px solid #f7c6cf;
    background-color: #fff5f7;
    border-radius: 0.5rem;
    padding: 0.75rem;
  }

  .error-news-title {
    font-weight: 600;
    color: #8c1d31;
    margin-bottom: 0.5rem;
  }

  .news-id-mode {
    padding: 0.75rem;
    border: 1px dashed #c9d2df;
    border-radius: 0.5rem;
    background-color: #fbfcfe;
  }

  .content-preview {
    max-width: 380px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0;
  }

  ::v-deep .candidate-news-table.table td,
  ::v-deep .candidate-news-table.table th {
    padding: 0.35rem 0.5rem;
    vertical-align: middle;
    line-height: 1.15;
  }

  ::v-deep .candidate-news-table .custom-checkbox {
    margin-bottom: 0;
  }

  .date-input-wrapper {
    .form-control {
      min-height: 38px;
    }
  }
}

.dark-layout {
  .vectorization-sync-page {
    .sync-card {
      background-color: #2a2f38;
      border: 0;
      border-radius: 0.75rem;
      box-shadow: 0 12px 24px rgba(34, 41, 47, 0.08);
    }

    .sync-card-header {
      background: linear-gradient(120deg, #232a32 0%, #2a2f38 100%);
      border-bottom: 1px solid #3b4253;
    }

    .news-id-mode {
      border-color: #3b4253;
      background-color: #232a32;
    }

    .result-success {
      background-color: rgba(40, 199, 111, 0.18);
      border-color: rgba(40, 199, 111, 0.35);
      color: #8be0b2;
    }

    .result-warning {
      background-color: rgba(255, 159, 67, 0.18);
      border-color: rgba(255, 159, 67, 0.35);
      color: #ffd08a;
    }

    .result-danger {
      background-color: rgba(234, 84, 85, 0.2);
      border-color: rgba(234, 84, 85, 0.35);
      color: #ff9da4;
    }

    .error-news-box {
      background-color: rgba(234, 84, 85, 0.12);
      border-color: rgba(234, 84, 85, 0.35);
    }

    .error-news-title {
      color: #ff9da4;
    }

    .date-input-wrapper {
      .form-control {
        background-color: #232a32;
        border: 1px solid #3b4253;
        color: #d0d2d6;
        min-height: 38px;
      }
    }
  }
}

@media (max-width: 767px) {
  .vectorization-sync-page {
    .sync-card-header {
      align-items: flex-start !important;
      flex-direction: column;
      gap: 0.5rem;
    }
  }
}
</style>